import React, { FormEvent } from 'react';
import { RouteComponentProps } from 'react-router';
import { connect } from 'react-redux';
import { ThunkDispatch } from 'redux-thunk';
import { Link } from 'react-router-dom';
import { UncontrolledTooltip as Tooltip } from 'reactstrap';
import { BrokerRateModel, DeskModel } from '../../../api/core/core.types';
import {
  DATE_FILTER_RANGES,
  ReservationFilters,
  ReservationModel,
  reservationStatusTypeOptions,
  TimeIntervalFilterValues
} from '../../../api/reservation/reservation.types';
import { AppState } from '../../../store/store.types';
import { getDeskList } from '../../setup/actions/desk.actions';
import { getReservationList, updateReservationFilters } from '../actions/reservation.actions';
import { ReservationActionTypes } from '../types/reservation.types';
import { convertArrayToQueryParameterArray } from '../../../helpers/parameter.formatter';
import TextOnlyButton from '../../../components/forms/TextOnlyButton';
import ReservationStatusLabel from '../../../components/forms/ReservationStatusLabel';
import Select from '../../../components/forms/Select';
import { DATE_TIME_FORMAT, PageRequest } from '../../../api/api.types';
import dayjs, { Dayjs } from 'dayjs';
import DatePicker from '../../../components/forms/DatePicker';
import { WithTranslation, withTranslation } from 'react-i18next';
import { LoaderBar } from '../../../components/forms/LoaderBar';
import { getAllBrokerRatesList } from '../../setup/actions/brokerrate.actions';
import ListViewTable from '../../../components/tables/ListViewTable';
import { SupplierBrokerModel } from '../../../api/admin/admin.types';
import { getSupplierBrokerList } from '../../admin/actions/supplierbroker.actions';

interface ReservationProps extends RouteComponentProps, WithTranslation {
  getDesks: () => void;
  getSupplierBrokers: () => void;
  getBrokerRates: () => void;
  getReservationsList: (filters?: ReservationFilters, pageRequest?: PageRequest) => Promise<void>;
  updateReservationFilters: (filters?: ReservationFilters) => void;
  reservations: ReservationModel[] | [];
  supplierBrokers: SupplierBrokerModel[];
  desks: DeskModel[];
  brokerRates: BrokerRateModel[] | [];
  totalPages: number;
  totalElements: number;
  filters: ReservationFilters | null;
}

interface ReservationState {
  filtersUpdated: boolean;
  selectedPageNumber: number;
  loading: boolean;
  filter: {
    generalFilter: {
      value: string;
    };
    reservationsBetween: { label: TimeIntervalFilterValues; from?: Dayjs; to?: Dayjs };
    dateFilter: {
      value: Dayjs | undefined;
    };
    reservationStatusTypeFilter: {
      value: { label: string; value: string }[];
    };
    pickupLocationFilter: {
      value: { label: string; value: string }[];
    };
    brokerFilter: {
      value: { label: string; value: string }[];
    };
    brokerRateFilter: {
      value: { label: string; value: string }[];
    }
  };
}

class Reservations extends React.Component<ReservationProps, ReservationState> {

  constructor(props: ReservationProps) {
    super(props);
    this.state = {
      filtersUpdated: false,
      selectedPageNumber: 1,
      loading: false,
      filter: {
        generalFilter: {
          value: ''
        },
        reservationsBetween: { label: TimeIntervalFilterValues.ALL, from: undefined, to: undefined },
        dateFilter: {
          value: undefined
        },
        reservationStatusTypeFilter: {
          value: []
        },
        pickupLocationFilter: {
          value: []
        },
        brokerFilter: {
          value: []
        },
        brokerRateFilter: {
          value: []
        }
      }
    };
  }

  async componentDidMount() {
    this.updateFilterValues();
    this.props.getSupplierBrokers();
    this.props.getDesks();
    this.props.getBrokerRates();
    this.setState({ loading: true });
    await this.props.getReservationsList(this.props.filters ? this.props.filters : undefined);
    this.setState({ loading: false });
  }

  componentDidUpdate() {
    if (!this.state.filtersUpdated) this.updateFilterValues();
  }

  updateFilterValues = () => {
    const filters = this.props.filters;
    this.setState({ filtersUpdated: true });

    if (filters) {
      const selectedReservationStatus = filters.reservationStatus ? reservationStatusTypeOptions.find((o) => o.value === filters.reservationStatus) : undefined;
      const selectedBroker = filters.brokerId ? this.props.supplierBrokers.find((b) => b.id === Number(filters.brokerId)) : undefined;
      const selectedDesks = filters.pickupDeskCodes ? filters.pickupDeskCodes : undefined;
      const selectedBrokerRate = filters.brokerRateCode ? this.props.brokerRates.find((b) => b.code === filters.brokerRateCode) : undefined;
      const selectedDeskOptions: { value: string; label: string }[] = [];
      if (selectedDesks) {
        const desks: string[] = selectedDesks.split(',');

        desks.forEach((d) => {
          const desk = this.props.desks.find((i) => i.code === d);
          selectedDeskOptions.push({ value: d, label: desk ? desk.name : d });
        });
      }

      const newState = {
        selectedPageNumber: filters.pageNumber ? filters.pageNumber : 1,
        filter: {
          generalFilter: {
            value: filters.combinedFilterField ? filters.combinedFilterField : ''
          },
          reservationsBetween: filters.reservationsBetween,
          dateFilter: {
            value: filters.date ? dayjs(filters.date) : undefined
          },
          reservationStatusTypeFilter: {
            value: selectedReservationStatus ? [ {
              value: selectedReservationStatus.value,
              label: selectedReservationStatus.label
            } ] : []
          },
          pickupLocationFilter: {
            value: selectedDeskOptions
          },
          brokerFilter: {
            value: selectedBroker ? [ {
              value: String(selectedBroker.id),
              label: selectedBroker.name
            } ] : []
          },
          brokerRateFilter: {
            value: selectedBrokerRate ? [ {
              value: selectedBrokerRate.code,
              label: selectedBrokerRate.code
            } ] : []
          }
        }
      };

      this.setState({ selectedPageNumber: newState.selectedPageNumber, filter: newState.filter });
    }
  };

  clearFilters = async () => {
    await this.setState({
      selectedPageNumber: 1,
      filter: {
        generalFilter: {
          value: ''
        },
        reservationsBetween: { label: TimeIntervalFilterValues.ALL, from: undefined, to: undefined },
        dateFilter: {
          value: undefined
        },
        reservationStatusTypeFilter: {
          value: []
        },
        pickupLocationFilter: {
          value: []
        },
        brokerFilter: {
          value: []
        },
        brokerRateFilter: {
          value: []
        }
      }
    });

    const filters = this.composeReservationFilters();
    this.props.updateReservationFilters(filters);
    this.setState({ loading: true });
    await this.props.getReservationsList(filters);
    this.setState({ loading: false });
  };

  onFilterClick = async (e: FormEvent) => {
    e.preventDefault();
    const filters = this.composeReservationFilters();
    this.setState({ loading: true });
    await this.props.getReservationsList(filters);
    this.setState({ loading: false });
    this.props.updateReservationFilters(filters);
  };

  handleUpdateResults = async () => {
    const filters = this.composeReservationFilters();
    this.setState({ loading: true });
    await this.props.getReservationsList(filters);
    this.setState({ loading: false });
    this.props.updateReservationFilters(filters);
  };

  composeReservationFilters = () => {
    const date = this.state.filter.dateFilter.value;
    let broker: any = this.state.filter.brokerFilter.value;
    let brokerRate: any = this.state.filter.brokerRateFilter.value;
    let reservationStatus: any = this.state.filter.reservationStatusTypeFilter.value;

    if (broker.length) {
      broker = broker[ 0 ];
    }

    if (reservationStatus.length) {
      reservationStatus = reservationStatus[ 0 ];
    }

    if (brokerRate.length) {
      brokerRate = brokerRate[ 0 ];
    }

    const desks: any[] = [];
    this.state.filter.pickupLocationFilter.value.forEach((v) => {
      desks.push(v.value);
    });

    const filters: ReservationFilters = {
      combinedFilterField: this.state.filter.generalFilter.value,

      date: date ? date.format('YYYY-MM-DD').concat('T08:00:00.0000') : undefined,
      reservationStatus: reservationStatus ? reservationStatus.value : undefined,
      brokerId: broker ? broker.value : undefined,
      brokerRateCode: brokerRate ? brokerRate.value : undefined,
      reservationsBetween: this.state.filter.reservationsBetween,
      pageNumber: this.state.selectedPageNumber,
      pickupDeskCodes: convertArrayToQueryParameterArray(desks)
    };

    return filters;
  };


  changeHandler = (event: { target: HTMLInputElement }) => {
    const { name, value } = event.target;
    this.setState({
      filter: {
        ...this.state.filter,
        [ name ]: {
          value
        }
      }
    });
  };

  dropdownChange = (newValue: any, field: string, isMulti: boolean) => {
    this.setState({
      filter: {
        ...this.state.filter,
        [ field ]: {
          value: newValue
        }
      }
    }, () => {
      if (!isMulti) {
        this.handleUpdateResults();
      }
    });
  };

  onDateChange = (date?: Dayjs) => {
    this.setState({
      filter: {
        ...this.state.filter,
        dateFilter: {
          value: date
        }
      }
    });
  };


  onTimeRangeChange = async (value: { from: Dayjs; to: Dayjs }, additionalValue: any) => {
    await this.setState({
      filter: {
        ...this.state.filter,
        reservationsBetween: {
          label: additionalValue,
          from: additionalValue !== TimeIntervalFilterValues.ALL ? value.from : undefined,
          to: additionalValue !== TimeIntervalFilterValues.ALL ? value.to : undefined
        }
      }
    });

    const filters = this.composeReservationFilters();

    this.props.updateReservationFilters(filters);
    this.props.getReservationsList(filters);
  };

  reservationNumberCell = (props: any) => <Link
    to={{ pathname: `/reservations/${props.original.reservationNumber}/view` }}>{`${(props.original.reservationNumber)}`}</Link>;

  reservationTimeCell = (props: any) =>
    <div>{`${dayjs(props.row.original.reservationTimestamp).format(DATE_TIME_FORMAT)}`}</div>;

  pickupCell = (props: any) => {
    const desk = this.props.desks.find((d) => props.row.original.pickupDeskCode === d.code);

    return <div>{`${(dayjs(props.row.original.pickupTimestamp).format(DATE_TIME_FORMAT))} @ ${desk ? desk.name : props.row.original.pickupDeskCode}`}</div>;
  };

  dropOffCell = (props: any) => {
    const desk = this.props.desks.find((d) => props.row.original.returnDeskCode === d.code);

    return <div>{`${(dayjs(props.row.original.returnTimestamp).format(DATE_TIME_FORMAT))} @ ${desk ? desk.name : props.row.original.returnDeskCode}`}</div>;
  };

  statusCell = (props: any) => <ReservationStatusLabel status={props.row.original.reservationStatusType}/>;

  onRowClick = (rowInfo: any) => this.props.history.push(`/reservations/${rowInfo.original.reservationNumber}/view`);


  brokerCell = (props: any) => {
    const broker = this.props.supplierBrokers.find((b) => props.row.original.brokerId === b.id);

    return <div>{broker ? broker.name : props.row.original.brokerId}</div>;
  };

  customerCell = (props: any) => (
    <div>{`${props.row.original.customer.firstName}  ${props.row.original.customer.lastName}`}</div>);

  getDataFn(pageRequest: PageRequest) {
    if (pageRequest.page !== undefined) {
      const pageRequestFix = {
        page: pageRequest.page
      };

      this.props.getReservationsList(this.composeReservationFilters(), pageRequestFix);
      this.setState({ selectedPageNumber: pageRequestFix.page + 1 });
    }
  }

  defineColumns() {
    const { t } = this.props;

    return [
      {
        header: t('ct.reservations.res_nr'),
        accessorKey: 'reservationNumber',
        id: 'reservationNumber',
        minWidth: 120,
        width: 120
      },
      {
        header: t('ct.reservations.res_time'),
        accessorKey: 'reservationTimestamp',
        id: 'reservationTimestamp',
        cell: this.reservationTimeCell,
        minWidth: 150,
        width: 150
      },
      {
        header: t('ct.reservations.pickup'),
        accessorKey: 'pickup',
        id: 'pickup',
        cell: this.pickupCell,
        minWidth: 300,
        width: 300
      },
      {
        header: t('ct.reservations.dropoff'),
        accessorKey: 'dropoff',
        cell: this.dropOffCell,
        minWidth: 300,
        width: 300
      },
      {
        header: t('ct.reservations.rental_days'),
        accessorKey: 'rentalDays',
        id: 'rentalDays',
        minWidth: 100,
        width: 100
      },
      {
        header: t('ct.reservations.broker_name'),
        accessorKey: 'brokerId',
        id: 'brokerId',
        cell: this.brokerCell
      },
      {
        header: t('ct.reservations.supplier_ref'), accessorKey: 'supplierReferenceCode', id: 'supplierReferenceCode'
      },
      {
        header: t('ct.reservations.customer'), accessorKey: 'customer', cell: this.customerCell, id: 'customer'
      },
      {
        header: t('ct.reservations.status'),
        accessorKey: 'reservationStatusType',
        cell: this.statusCell,
        id: 'reservationStatusType'
      }
    ];
  }

  render() {
    const { t } = this.props;

    return (
      <main>
        <div className="page-content">
          <div className="d-flex justify-content-between align-items-center">
            <h1>{t('page_title.reservations')}</h1>
          </div>
          <form className="container-fluid filter-container form-group form-inline d-flex justify-content-between">
            <div className="d-flex row flex-wrap filters-container ml-1">
              <input
                value={this.state.filter.generalFilter.value}
                type="text"
                name="generalFilter"
                className="form-control col filter-select margins filter-item-margin"
                placeholder={t('ph.filter.reservations.search')}
                onChange={this.changeHandler}
                id="reservation-search"
              />
              <Tooltip
                modifiers={{ preventOverflow: { boundariesElement: 'window' } }}
                placement="bottom"
                target="reservation-search"
                autohide={false}
              >
                <div className="d-flex flex-wrap">
                  {t('comment.reservations.search')}
                </div>
              </Tooltip>
              <Select
                value={this.state.filter.brokerFilter.value}
                className="mr-1"
                placeholder={t('ph.filter.reservations.by_broker')}
                equalWidthElement
                marginTop
                // eslint-disable-next-line no-nested-ternary
                options={this.props.supplierBrokers.sort((i, b) => ((i.name < b.name) ? -1 : (i.name > b.name) ? 1 : 0)).map((b) => ({
                  label: b.name,
                  value: String(b.id)
                }))}
                onChange={(val) => this.dropdownChange(val, 'brokerFilter', false)}
                closeMenuOnSelect={false}
                hideSelectedOptions={false}
                label="broker"
              />
              <Select
                value={this.state.filter.brokerRateFilter.value}
                className="mr-1"
                placeholder={t('ph.filter.reservations.by_broker_rate')}
                equalWidthElement
                marginTop
                // eslint-disable-next-line no-nested-ternary
                options={this.props.brokerRates.sort((i, b) => ((i.code < b.code) ? -1 : (i.code > b.code) ? 1 : 0)).map((b) => ({
                  label: b.code,
                  value: b.code
                }))}
                onChange={(val) => this.dropdownChange(val, 'brokerRateFilter', false)}
                closeMenuOnSelect={false}
                hideSelectedOptions={false}
                label="brokerRateCode"
              />
              <Select
                value={this.state.filter.reservationStatusTypeFilter.value}
                equalWidthElement
                marginTop
                placeholder={t('ph.filter.reservations.by_reservation_status')}
                options={reservationStatusTypeOptions}
                onChange={(val) => this.dropdownChange(val, 'reservationStatusTypeFilter', false)}
                closeMenuOnSelect={false}
                hideSelectedOptions={false}
                label="reservationStatus"
              />
              <div className="margins-form-select ml-0">
                <Select
                  value={this.state.filter.pickupLocationFilter.value}
                  elWidth={250}
                  marginTop
                  placeholder={t('ph.filter.reservations.by_pickup_location')}
                  // eslint-disable-next-line no-nested-ternary
                  options={this.props.desks.sort((i, d) => ((i.name < d.name) ? -1 : (i.name > d.name) ? 1 : 0)).map((d) => ({
                    label: d.name,
                    value: d.code
                  }))}
                  onChange={(val) => this.dropdownChange(val, 'pickupLocationFilter', true)}
                  handleBlurChanged={() => {
                    this.handleUpdateResults();
                  }}
                  closeMenuOnSelect={false}
                  isMulti
                  hideSelectedOptions={false}
                  label="pickupLocation"
                />
              </div>
              <div className="mr-1 filter-item-margin">
                <DatePicker
                  id="reservation-pickup-date-filter"
                  isEdit
                  isFilter
                  date={this.state.filter ? this.state.filter.dateFilter.value : undefined}
                  onChange={(val) => this.onDateChange(val)}
                />
                <Tooltip
                  modifiers={{ preventOverflow: { boundariesElement: 'window' } }}
                  placement="bottom"
                  target="reservation-pickup-date-filter"
                  autohide={false}
                >
                  <div className="d-flex flex-wrap">
                    {t('comment.reservations.by_pickup_date')}
                  </div>
                </Tooltip>
              </div>
            </div>
            <div>
              <button type="button" className="btn btn-text-only mr-1" onClick={() => this.clearFilters()}>
                {t('b.clear_filter')}
              </button>
              <button type="submit" className="btn btn-primary filter-button" onClick={this.onFilterClick}>
                {t('b.filter').toUpperCase()}
              </button>
            </div>
          </form>
          <div className="d-flex mb-1">
            <label className="font-weight-bold  mr-2">{t('l.reservations.show_reservations_created')}:</label>
            <TextOnlyButton
              active={TimeIntervalFilterValues.TODAY === this.state.filter.reservationsBetween.label}
              onClick={this.onTimeRangeChange}
              value={DATE_FILTER_RANGES.TODAY}
              additionalValue={TimeIntervalFilterValues.TODAY}
              className="btn btn-text-only"
              text={t('text.reservations.today')}
            />
            <TextOnlyButton
              active={TimeIntervalFilterValues.YESTERDAY === this.state.filter.reservationsBetween.label}
              onClick={this.onTimeRangeChange}
              value={DATE_FILTER_RANGES.YESTERDAY}
              additionalValue={TimeIntervalFilterValues.YESTERDAY}
              className="btn btn-text-only"
              text={t('text.reservations.since_yesterday')}
            />
            <TextOnlyButton
              active={TimeIntervalFilterValues.WEEK === this.state.filter.reservationsBetween.label}
              onClick={this.onTimeRangeChange}
              value={DATE_FILTER_RANGES.WEEK}
              additionalValue={TimeIntervalFilterValues.WEEK}
              className="btn btn-text-only"
              text={t('text.reservations.last_7_days')}
            />
            <TextOnlyButton
              active={TimeIntervalFilterValues.MONTH === this.state.filter.reservationsBetween.label}
              onClick={this.onTimeRangeChange}
              value={DATE_FILTER_RANGES.MONTH}
              additionalValue={TimeIntervalFilterValues.MONTH}
              className="btn btn-text-only"
              text={t('text.reservations.current_month')}
            />
            <TextOnlyButton
              active={TimeIntervalFilterValues.LASTMONTH === this.state.filter.reservationsBetween.label}
              onClick={this.onTimeRangeChange}
              value={DATE_FILTER_RANGES.LASTMONTH}
              additionalValue={TimeIntervalFilterValues.LASTMONTH}
              className="btn btn-text-only"
              text={t('text.reservations.last_month')}
            />
            <TextOnlyButton
              active={TimeIntervalFilterValues.ALL === this.state.filter.reservationsBetween.label}
              onClick={this.onTimeRangeChange}
              value={DATE_FILTER_RANGES.ALL}
              additionalValue={TimeIntervalFilterValues.ALL}
              className="btn btn-text-only"
              text={t('text.reservations.all_time')}
            />
          </div>
          {this.state.loading ? <LoaderBar/> : null}
          {this.props.reservations
            ? (
              <div className="table-list">
                <ListViewTable
                  data={this.props.reservations}
                  columns={this.defineColumns()}
                  getTrProps={this.onRowClick}
                  paginationEnabled={true}
                  totalPages={this.props.totalPages}
                  totalElements={this.props.totalElements}
                  selectedPageNumber={this.state.selectedPageNumber}
                  getDataFn={(pageRequest: PageRequest) => this.getDataFn(pageRequest)}
                />
              </div>
            ) : null}
        </div>
      </main>
    );
  }
}

const mapStateToProps = (state: AppState) => ({
  reservations: state.reservation.reservations.reservations,
  totalPages: state.reservation.reservations.totalPages,
  totalElements: state.reservation.reservations.totalElements,
  filters: state.reservation.reservations.filters,
  supplierBrokers: state.admin.supplierBrokers.supplierBrokers,
  brokerRates: state.core.brokerRates.brokerRates,
  desks: state.setup.desks.desks
});

const mapDispatchToProps = (dispatch: ThunkDispatch<{}, {}, ReservationActionTypes>) => ({
  getReservationsList: (filters?: ReservationFilters, pageRequest?: PageRequest) => dispatch(getReservationList(filters, pageRequest)),
  updateReservationFilters: (filters?: ReservationFilters) => dispatch(updateReservationFilters(filters)),
  getDesks: () => dispatch(getDeskList()),
  getSupplierBrokers: () => dispatch(getSupplierBrokerList()),
  getBrokerRates: () => dispatch(getAllBrokerRatesList())
});

export default withTranslation()(connect(
  mapStateToProps,
  mapDispatchToProps
)(Reservations));
